import { apiSlice, createClientV2QueryFn } from 'store/slices/api';
import { QueryParams } from 'types/api';
import { parseQueryParams } from 'store/slices/utils';
import { KitRequestSchema } from 'types/kitting';
import {
  PurchaseOrderSchemaClientV2,
  SupplierBillLineSchema,
  SupplierBillSchema,
} from 'types/procurement';
import {
  SupplierQuoteSchema,
  SupplierQuoteLineSchema,
  RfqSchema,
} from 'types/sourcing';
import { AllocationSchema } from 'types/clientV2/allocations';
import { PurchaseRequestGroupSchema } from 'types/clientV2/purchaseRequests';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';
import { ShipmentSchema } from 'types/clientV2/shipments';
import {
  OrgPartSchema,
  PublicPartAssetSchema,
  PublicPartSchema,
} from 'types/clientV2/parts';

const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getExportAllocations: builder.query<Blob, QueryParams<AllocationSchema>>({
      queryFn: createClientV2QueryFn<Blob, QueryParams<AllocationSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.ALLOCATIONS(
            parseQueryParams<AllocationSchema>({ ...params, exportBlob: true })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    getExportPurchaseOrders: builder.query<
      Blob,
      QueryParams<PurchaseOrderSchemaClientV2>
    >({
      queryFn: createClientV2QueryFn<
        Blob,
        QueryParams<PurchaseOrderSchemaClientV2>
      >((params) => ({
        url: CLIENT_V2_URLS.PURCHASE_ORDERS(
          parseQueryParams<PurchaseOrderSchemaClientV2>({
            ...params,
            exportBlob: true,
          })
        ),
        method: 'GET',
        responseType: 'blob',
        responseHandler: (response) => response.blob(),
      })),
    }),
    getExportSupplierBills: builder.query<
      Blob,
      QueryParams<SupplierBillSchema>
    >({
      queryFn: createClientV2QueryFn<Blob, QueryParams<SupplierBillSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.SUPPLIER_BILLS(
            parseQueryParams<SupplierBillSchema>({
              ...params,
              exportBlob: true,
            })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    getExportSupplierBillLines: builder.query<
      Blob,
      QueryParams<SupplierBillLineSchema>
    >({
      queryFn: createClientV2QueryFn<Blob, QueryParams<SupplierBillLineSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.SUPPLIER_BILL_LINES(
            parseQueryParams<SupplierBillLineSchema>({
              ...params,
              exportBlob: true,
            })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    getExportKitRequests: builder.query<Blob, QueryParams<KitRequestSchema>>({
      queryFn: createClientV2QueryFn<Blob, QueryParams<KitRequestSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.KIT_REQUESTS(
            parseQueryParams<KitRequestSchema>({ ...params, exportBlob: true })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    getExportShipments: builder.query<Blob, QueryParams<ShipmentSchema>>({
      queryFn: createClientV2QueryFn<Blob, QueryParams<ShipmentSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.SHIPMENTS(
            parseQueryParams<ShipmentSchema>({ ...params, exportBlob: true })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    getExportPurchaseRequestGroups: builder.query<
      Blob,
      QueryParams<PurchaseRequestGroupSchema>
    >({
      queryFn: createClientV2QueryFn<
        Blob,
        QueryParams<PurchaseRequestGroupSchema>
      >((params) => ({
        url: CLIENT_V2_URLS.PURCHASE_REQUEST_GROUPS(
          parseQueryParams<PurchaseRequestGroupSchema>({
            ...params,
            exportBlob: true,
          })
        ),
        method: 'GET',
        responseType: 'blob',
        responseHandler: (response) => response.blob(),
      })),
    }),
    getExportSupplierQuotes: builder.query<
      Blob,
      QueryParams<SupplierQuoteSchema>
    >({
      queryFn: createClientV2QueryFn<Blob, QueryParams<SupplierQuoteSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.SUPPLIER_QUOTES(
            parseQueryParams<SupplierQuoteSchema>({
              ...params,
              exportBlob: true,
            })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    getExportRfqs: builder.query<Blob, QueryParams<RfqSchema>>({
      queryFn: createClientV2QueryFn<Blob, QueryParams<RfqSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.RFQS(
            parseQueryParams<RfqSchema>({ ...params, exportBlob: true })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    getExportSupplierQuoteLines: builder.query<
      Blob,
      QueryParams<SupplierQuoteLineSchema>
    >({
      queryFn: createClientV2QueryFn<
        Blob,
        QueryParams<SupplierQuoteLineSchema>
      >((params) => ({
        url: CLIENT_V2_URLS.SUPPLIER_QUOTE_LINES(
          parseQueryParams<SupplierQuoteLineSchema>({
            ...params,
            exportBlob: true,
          })
        ),
        method: 'GET',
        responseType: 'blob',
        responseHandler: (response) => response.blob(),
      })),
    }),
    getExportOrgParts: builder.query<Blob, QueryParams<OrgPartSchema>>({
      queryFn: createClientV2QueryFn<Blob, QueryParams<OrgPartSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.ORG_PARTS(
            parseQueryParams<OrgPartSchema>({ ...params, exportBlob: true })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    getExportPublicParts: builder.query<Blob, QueryParams<PublicPartSchema>>({
      queryFn: createClientV2QueryFn<Blob, QueryParams<PublicPartSchema>>(
        (params) => ({
          url: CLIENT_V2_URLS.PUBLIC_PARTS(
            parseQueryParams<PublicPartSchema>({
              ...params,
              exportBlob: true,
            })
          ),
          method: 'GET',
          responseType: 'blob',
          responseHandler: (response) => response.blob(),
        })
      ),
    }),
    downloadPublicPartAsset: builder.query<Blob, PublicPartAssetSchema['id']>({
      query: (assetId) => ({
        url: CLIENT_V2_URLS.PUBLIC_PART_ASSETS_DOWNLOAD(assetId),
        method: 'GET',
        responseType: 'blob',
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});
export const {
  useLazyGetExportAllocationsQuery,
  useLazyGetExportSupplierBillsQuery,
  useLazyGetExportSupplierBillLinesQuery,
  useLazyGetExportKitRequestsQuery,
  useLazyGetExportShipmentsQuery,
  useLazyGetExportPurchaseRequestGroupsQuery,
  useLazyGetExportSupplierQuotesQuery,
  useLazyGetExportSupplierQuoteLinesQuery,
  useLazyGetExportRfqsQuery,
  useLazyGetExportPurchaseOrdersQuery,
  useLazyGetExportOrgPartsQuery,
  useLazyGetExportPublicPartsQuery,
  useDownloadPublicPartAssetQuery,
  useLazyDownloadPublicPartAssetQuery,
} = extendedApiSlice;
